
import {Options, Vue} from 'vue-class-component';
import {RestService} from "../service/rest.service";
import PaginationComponent from '@/components/pagination/PaginationComponent.vue';

@Options({
  components: {
    PaginationComponent
  }
})
export default class StartupResourcesView extends Vue {

  restService = new RestService('startup/resources')

  companies: any[] = []
  filtered: any[] = []
  page = 1
  pageSize = 10

  // 資料載入完成後即顯示全部團隊
  searched = false

  // 繁體中文依筆劃排序，同筆劃時由 collator 提供穩定的次序
  companyNameCollator = new Intl.Collator('zh-Hant-TW-u-co-stroke', {
    usage: 'sort',
    sensitivity: 'base',
    numeric: true
  })

  // 區分「未下條件」的空值與「已下條件，但全選」的不限
  allOptionValue = '__ALL__'

  searchForm: any = {
    techField: '',
    city: '',
    keyword: ''
  }

  // 計畫別對應：標籤文字與配色（顏色比照原型）
  planMap: any = {
    AIOT: {label: 'AIoT加速器', cls: 'btn-primay'},
    STARTUP: {label: '青創基地', cls: 'btn-secondary'},
    HUB: {label: '社創基地', cls: 'btn-orange'}
  }

  // 計畫優先序：AIoT加速器 > 社創基地 > 青創基地；用於合併同名企業時挑選代表資料與排序計畫標籤
  planOrder = ['AIOT', 'HUB', 'STARTUP']

  cityList = [
    '新竹縣', '新竹市', '臺北市', '新北市', '桃園市', '臺中市', '臺南市', '高雄市',
    '基隆市', '苗栗縣', '彰化縣', '南投縣', '雲林縣', '嘉義市', '嘉義縣', '屏東縣',
    '宜蘭縣', '花蓮縣', '臺東縣', '澎湖縣', '金門縣', '連江縣'
  ]

  techFieldList = [
    '智慧製造', '智慧醫療', '智慧城市', '智慧零售', '淨零碳排/能源',
    '資安應用', '智慧物流/倉儲', '智慧農業', '其他跨域應用'
  ]

  selected: any = null

  // 進入頁面後預設顯示全部團隊
  mounted() {
    this.restService.gets().then((data: any) => {
      this.companies = Array.isArray(data) ? data : []
      this.filtered = this.mergedCompanies
      this.searched = true
    })
  }

  get paged() {
    const start = (this.page - 1) * this.pageSize
    return this.filtered.slice(start, start + this.pageSize)
  }

  get totalPages() {
    return Math.ceil(this.filtered.length / this.pageSize)
  }

  // 所在城市選項：只列出資料中實際有值的城市，依行政區順序排列
  get cityOptions(): string[] {
    const present = new Set(this.companies.map((c) => c.city).filter(Boolean))
    const ordered = this.cityList.filter((c) => present.has(c))
    const extras = [...present].filter((c) => !this.cityList.includes(c))
    return [...ordered, ...extras]
  }

  // 是否已設定任一查詢條件（技術應用領域 / 所在城市 / 關鍵字）；未下條件時查詢按鈕停用
  get hasSearchCondition(): boolean {
    const {techField, city, keyword} = this.searchForm
    return !!(techField || city || (keyword || '').trim())
  }

  // 計畫優先序名次：數字越小優先度越高；不在清單者排最後
  planRank(plan: string): number {
    const i = this.planOrder.indexOf(plan)
    return i === -1 ? this.planOrder.length : i
  }

  // 依「企業名稱」合併多筆計畫資料：代表資料取優先序最高的計畫，並彙整所有計畫標籤（plans）
  // 保留該企業的所有原始計畫資料（records）供查詢比對使用
  get mergedCompanies(): any[] {
    const map = new Map<string, any>()
    for (const c of this.companies) {
      const key = c.name || ''
      const existing = map.get(key)
      if (!existing) {
        map.set(key, {...c, plans: [c.plan], records: [c]})
      } else {
        existing.records.push(c)
        if (!existing.plans.includes(c.plan)) existing.plans.push(c.plan)
        // 此筆計畫優先序更高時，改用其資料作為代表（保留已彙整的 plans / records）
        if (this.planRank(c.plan) < this.planRank(existing.plan)) {
          map.set(key, {...c, plans: existing.plans, records: existing.records})
        }
      }
    }
    // 每間企業的計畫標籤依優先序排列
    const list = [...map.values()]
    list.forEach((e) => e.plans.sort((a: string, b: string) => this.planRank(a) - this.planRank(b)))
    return list.sort((a, b) => this.companyNameCollator.compare(a.name || '', b.name || ''))
  }

  search() {
    const {techField, city, keyword} = this.searchForm
    const kw = (keyword || '').trim().toLowerCase()

    // 企業任一計畫的資料符合條件即納入結果
    this.filtered = this.mergedCompanies.filter((e) => {
      return e.records.some((c: any) => {
        const tags: string[] = c.techFields || []
        const matchTech = !techField || techField === this.allOptionValue || tags.includes(techField)
        const matchCity = !city || city === this.allOptionValue || c.city === city
        const matchKeyword = !kw
            || (c.name || '').toLowerCase().includes(kw)
            || (c.description || '').toLowerCase().includes(kw)
        return matchTech && matchCity && matchKeyword
      })
    })
    this.page = 1
    this.searched = true
  }

  changePage(page: number) {
    this.page = page
    window.scrollTo({top: 0})
  }

  select(company: any) {
    this.selected = company
  }

  planLabel(plan: string) {
    return this.planMap[plan]?.label || ''
  }

  planClass(plan: string) {
    return this.planMap[plan]?.cls || 'btn-primay'
  }

  // 依「技術應用領域」下拉選單的順序排序；不在選單清單中的項目排在最後
  sortByTechField(tags: string[]): string[] {
    const order = (t: string) => {
      const i = this.techFieldList.indexOf(t)
      return i === -1 ? this.techFieldList.length : i
    }
    return [...tags].sort((a, b) => order(a) - order(b))
  }

  // 列表標籤：網頁版最多 3 個（第 3 個手機版隱藏）
  listTags(company: any): string[] {
    return this.sortByTechField(company.techFields || []).slice(0, 3)
  }

  // 彈窗標籤：全部顯示
  modalTags(company: any): string[] {
    return this.sortByTechField(company.techFields || [])
  }
}
